import { ArrowRight, ThermometerHot, WifiHigh, Wind } from "@phosphor-icons/react";

interface GatewayCardProps {
  icon: typeof ThermometerHot;
  label: string;
  system: string;
  accent: "orange" | "blue";
}

function GatewayCard({ icon: Icon, label, system, accent }: GatewayCardProps) {
  const accentClasses =
    accent === "orange" ? "bg-daylight-subtle text-accent" : "bg-sky-subtle text-sky";

  return (
    <article className="relative grid min-h-[174px] grid-cols-[1fr_170px] overflow-hidden rounded-2xl border border-white/80 bg-white/90 shadow-[0_14px_40px_-24px_rgba(40,35,31,0.45)] backdrop-blur sm:grid-cols-[1fr_196px]">
      <div className="z-[1] flex flex-col justify-between p-5 pr-0 sm:p-6 sm:pr-0">
        <div className={`flex size-10 items-center justify-center rounded-full ${accentClasses}`}>
          <Icon className="size-5" weight="light" aria-hidden />
        </div>
        <div>
          <p className="text-muted mb-1 text-[12px] tracking-[0.08em] uppercase">{label}</p>
          <h3 className="text-primary text-[18px] leading-6">{system}</h3>
          <p className="text-muted mt-1 text-[13px] leading-5">USR-W610 connector</p>
        </div>
      </div>
      <div className="relative flex items-center justify-center overflow-hidden">
        <div className="bg-background absolute right-[-50px] size-[210px] rounded-full" />
        {/* PLACEHOLDER ASSET. The design calls for a USR-W610 render at
            /brand/onboarding/usr-gateway.png, which does not exist yet. Swap
            this src once Nico supplies it, and give each card its own alt. */}
        <img
          src="/brand/onboarding/gateway-wavin.png"
          alt="Wi-Fi connector"
          className="relative right-[-14px] w-[178px] rotate-[-4deg] mix-blend-multiply sm:w-[194px]"
        />
      </div>
    </article>
  );
}

export function KitExplainer({ onShowSetup }: { onShowSetup: () => void }) {
  return (
    <aside className="bg-surface-subtle relative overflow-hidden rounded-[28px] p-5 sm:p-8 lg:min-h-[690px] lg:p-10">
      {/* Flat Background-Blue block instead of the blurred halo disc — the
          panel is a block now, and a soft-edged wash was the one thing on it
          still pretending to be lit from somewhere. */}
      <div className="bg-sky-subtle absolute inset-x-0 top-0 h-72" aria-hidden />
      <div className="relative">
        <p className="text-primary mb-3 flex items-center gap-2 text-[12px] tracking-[0.1em] uppercase">
          <span className="bg-accent block size-2 rounded-full" />
          Your kit, prepared for your home
        </p>
        <h2 className="text-primary max-w-[520px] text-[30px] leading-[1.15] tracking-[-0.02em] sm:text-[36px]">
          Two connectors. One home network.
        </h2>
        <p className="text-muted mt-4 max-w-[560px] text-[16px] leading-6">
          These connectors link your heating and ventilation to the Smart Climate hub. We set both
          up before shipping so installation at home stays plug-and-play.
        </p>

        <div className="relative mt-8 space-y-3">
          <div className="bg-faint absolute top-[150px] bottom-[150px] left-[-13px] w-px sm:left-[-17px]" />
          <GatewayCard
            icon={ThermometerHot}
            label="Connector 1 of 2"
            system="Wavin floor heating"
            accent="orange"
          />
          <GatewayCard
            icon={Wind}
            label="Connector 2 of 2"
            system="Nilan ventilation"
            accent="blue"
          />
        </div>

        <div className="bg-primary mt-5 flex flex-col gap-3 rounded-2xl p-5 text-white sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-white/10">
              <WifiHigh className="size-5" weight="light" aria-hidden />
            </div>
            <div>
              <p className="text-[15px] leading-5">Both connect to your main Wi-Fi</p>
              <p className="mt-1 text-[13px] leading-5 text-white/60">
                So they can reach the Smart Climate hub on the same network.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onShowSetup}
            className="group flex shrink-0 items-center gap-2 self-start rounded-md border border-white/20 px-4 py-2.5 text-[13px] transition hover:border-white/50 hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:self-center"
          >
            See how we set them up
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </aside>
  );
}
